import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  LabelList
} from 'recharts';
import { pluralize } from '../../utils/textUtils';

// Типы данных, как в ComplianceChart.fixed.tsx и chartDataProcessor.js
interface ChartItemUI { // Это будет ChartItem из ProcessedChartData
  name: string;
  total: number;
  violations: number;
  percentage: number; 
  displayPercentage: string; // Это значение готовит processChartData
  category: string; 
  // gradient для SVG <defs> будет выбран по category, CSS-градиент не нужен в этом интерфейсе
}

interface ProcessedChartDataUI {
  hasData: boolean;
  mainTitle?: string;
  items?: ChartItemUI[];
  compliantFoundersList?: string[];
  totalViolations?: number;
  dateDisplayInfo?: string;
  startDate?: string;
  endDate?: string;
}

interface ResponsiveChartProps {
  processedData: ProcessedChartDataUI;
  chartType: 'omsu' | 'iogv'; // Для условных стилей, если понадобятся позже для UI
}

const UI_COLORS = {
  red: '#D32F2F',
  yellow: '#F9A825',
  green: '#2E7D32',
  axisColor: '#64748B', 
  gridColor: '#EEF2F6', // Более светлый для фона сетки, если нужна
  gridStroke: '#E5E7EB', // Чуть более заметный пунктир, чем gridColor
  neutralBg: '#F8FAFC', 
  neutralCard: '#FFFFFF',
  textHeading: '#1A2A42',
  textBody: '#334665',
  textMuted: '#64748B',
  textBright: '#0F172A',
  greenLightBg: '#E8F5E9' // для блока выполнивших норму
};

// Возвращаем useContainerWidth и getResponsiveChartUiStyles
const useContainerWidth = (ref: React.RefObject<HTMLDivElement>) => {
  const [width, setWidth] = useState<number | undefined>();
  
  const handleResize = useCallback(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, [ref]);

  useEffect(() => {
    const currentRef = ref.current; 
    handleResize();
    window.addEventListener('resize', handleResize);
    let resizeObserver: ResizeObserver | null = null;
    if (currentRef) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(currentRef);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && currentRef) {
        resizeObserver.unobserve(currentRef);
      }
    };
  }, [ref, handleResize]); 
  
  return width;
};

const getResponsiveChartUiStyles = (containerWidth?: number, isSmallScreen?: boolean, verySmallScreen?: boolean) => { 
  // isSmallScreen и verySmallScreen будут использоваться для адаптивных стилей ниже
  return {
    container: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      padding: isSmallScreen ? '10px' : '20px', // Пример адаптивного отступа
    },
    heading: {
      color: UI_COLORS.textHeading,
      letterSpacing: '-0.025em',
      fontWeight: 700, 
      fontSize: verySmallScreen? '0.9rem' : (isSmallScreen ? '1.1rem' : '1.25rem'), 
      lineHeight: '1.4',
      marginBottom: isSmallScreen ? '0.75rem' : '1rem',
      textAlign: 'left' as const,
    },
    legendContainer: {
      display: 'flex',
      gap: isSmallScreen ? '1rem' : '1.5rem',
      marginBottom: isSmallScreen ? '1rem' : '1.25rem',
      padding: isSmallScreen ? '0.5rem 0.75rem' : '0.75rem 1rem',
      borderRadius: '6px',
      backgroundColor: UI_COLORS.neutralCard,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      alignItems: 'center',
      flexWrap: 'wrap' as const, 
      justifyContent: isSmallScreen ? 'center' : 'flex-start',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
      fontWeight: 500,
      color: UI_COLORS.textBody,
    },
    legendColorBox: {
      width: isSmallScreen ? '0.75rem' : '1rem',
      height: isSmallScreen ? '0.75rem' : '1rem',
      borderRadius: '4px',
    },
    chartWrapper: {
      backgroundColor: UI_COLORS.neutralCard,
      borderRadius: '6px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      overflow: 'hidden', 
      padding: verySmallScreen ? '0.25rem' : (isSmallScreen ? '0.5rem' : '1rem'), // Адаптивный внутренний отступ
    },
    infoBlocksContainer: {
      display: 'flex',
      flexDirection: verySmallScreen ? 'column' : 'row', // Столбец на очень маленьких экранах
      justifyContent: 'space-between',
      alignItems: verySmallScreen ? 'stretch' : 'center',
      marginTop: '1.25rem',
      marginBottom: '0.75rem',
      gap: '1rem',
    },
    totalBlock: {
      backgroundColor: UI_COLORS.neutralCard,
      borderRadius: '999px', 
      padding: isSmallScreen ? '0.375rem 0.75rem' : '0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      flexGrow: verySmallScreen ? 0 : 1, // Не растягивать, если в колонке
      justifyContent: 'center',
      minWidth: '200px', // Оставляем, чтобы не сжимались слишком сильно
      width: verySmallScreen ? '100%' : 'auto',
    },
    totalText: {
      fontWeight: 500,
      color: UI_COLORS.textBody,
    },
    totalNumber: {
      fontWeight: 600,
      color: UI_COLORS.textBright,
    },
    dateBlock: {
      backgroundColor: UI_COLORS.neutralCard,
      borderRadius: '6px',
      padding: isSmallScreen ? '0.375rem 0.75rem' : '0.5rem 1rem',
      fontSize: verySmallScreen ? '0.7rem' : (isSmallScreen ? '0.75rem' : '0.75rem'),
      color: UI_COLORS.textMuted,
      fontWeight: 400,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      textAlign: 'center' as const,
      flexGrow: verySmallScreen ? 0 : 1,
      minWidth: '200px',
      width: verySmallScreen ? '100%' : 'auto',
    },
    compliantBlock: {
      backgroundColor: UI_COLORS.greenLightBg,
      borderRadius: '6px',
      padding: isSmallScreen ? '0.75rem' : '1rem',
      marginTop: '1.25rem',
      borderLeft: `4px solid ${UI_COLORS.green}`,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    },
    compliantHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.5rem',
      gap: '0.5rem'
    },
    compliantTitle: {
      fontSize: isSmallScreen ? '0.825rem' : '0.875rem',
      fontWeight: 600,
      color: UI_COLORS.textBright,
    },
    compliantList: {
      fontSize: isSmallScreen ? '0.825rem' : '0.875rem',
      color: UI_COLORS.green,
      fontWeight: 500,
      lineHeight: '1.6',
      paddingLeft: '0.5rem', 
    }
  };
};

const BAR_HEIGHT = 28; // Высота каждого бара в px
const CHART_TOP_PADDING = 10;
const CHART_BOTTOM_PADDING = 10; // Уменьшаем, т.к. XAxis будет скрыта
const Y_AXIS_WIDTH_OMSU = 180; 
const Y_AXIS_WIDTH_IOGV = 250; // Увеличиваем для ИОГВ
const Y_AXIS_MAX_LABEL_LENGTH = 33; // Макс. длина метки YAxis перед обрезкой

const CustomYAxisTick = (props: any) => {
  const { x, y, payload, isSmallScreen, verySmallScreen } = props; 
  
  if (verySmallScreen) { // Полностью скрываем метки на очень узких экранах
    return null;
  }

  let label = payload.value;
  const currentMaxLabelLength = isSmallScreen ? 18 : Y_AXIS_MAX_LABEL_LENGTH; // Короче на small экранах

  if (label.length > currentMaxLabelLength) {
    label = `${label.substring(0, currentMaxLabelLength)}...`;
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dx={-10} dy={4} textAnchor="end" fill={UI_COLORS.textBody} fontSize={isSmallScreen ? 10 : 12}>
        {label}
      </text>
    </g>
  );
};

const ResponsiveComplianceChart: React.FC<ResponsiveChartProps> = ({ processedData, chartType }) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(chartContainerRef);
  const isSmallScreen = containerWidth ? containerWidth < 768 : false;
  const verySmallScreen = containerWidth ? containerWidth < 480 : false;
  const styles = getResponsiveChartUiStyles(containerWidth, isSmallScreen, verySmallScreen);

  const { 
    mainTitle, 
    items,
    compliantFoundersList = [], 
    totalViolations = 0, 
    dateDisplayInfo
  } = processedData || {};

  if (!processedData || !processedData.hasData || !items || items.length === 0) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Нет данных для отображения графика.</div>;
  }

  const yAxisCurrentWidth = verySmallScreen ? 20 : (chartType === 'iogv' ? Y_AXIS_WIDTH_IOGV : Y_AXIS_WIDTH_OMSU);
  const calculatedHeight = items.length * (BAR_HEIGHT + (isSmallScreen ? 6 : 8)) + CHART_TOP_PADDING + CHART_BOTTOM_PADDING + 40; 

  const legendPayload = [
    { value: 'Высокий процент', type: 'square', id: 'high', color: UI_COLORS.red },
    { value: 'Средний процент', type: 'square', id: 'medium', color: UI_COLORS.yellow },
    { value: 'Низкий процент', type: 'square', id: 'low', color: UI_COLORS.green },
  ];
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const itemData = payload[0].payload as ChartItemUI;
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          fontSize: '0.875rem' 
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '5px', color: UI_COLORS.textBright }}>{itemData.name}</p>
          <p style={{color: UI_COLORS.textBody}}>{`Нарушения: ${itemData.violations} из ${itemData.total}`}</p>
          <p style={{color: UI_COLORS.textBody}}>{`Процент: ${itemData.displayPercentage}%`}</p>
        </div>
      );
    }
    return null;
  };

  const TotalViolationsIcon = () => ( <svg style={{color: UI_COLORS.red, width: 16, height: 16, flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor"> <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /> </svg> );
  const CompliantIcon = () => ( <svg style={{color: UI_COLORS.green, width: 16, height: 16, flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor"> <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /> </svg> );

  const getGradientId = (category: string) => {
    if (category === 'high') return 'url(#barGradientRed)';
    if (category === 'medium') return 'url(#barGradientYellow)';
    if (category === 'low' || category === 'none') return 'url(#barGradientGreen)';
    return UI_COLORS.green; // Fallback
  };

  return (
    <div ref={chartContainerRef} style={styles.container}>
      {mainTitle && <h2 style={styles.heading}>{mainTitle}</h2>}
      
      {!verySmallScreen && (
        <div style={styles.legendContainer}>
          {legendPayload.map(entry => (
            <div key={entry.id} style={styles.legendItem}>
              <div style={{...styles.legendColorBox, backgroundColor: entry.color as string }}></div>
              <span>{entry.value}</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={calculatedHeight}>
          <BarChart
            data={items}
            layout="vertical"
            margin={{ 
              top: CHART_TOP_PADDING, 
              right: verySmallScreen ? 20 : 50, // Меньше отступ для LabelList на verySmallScreen
              left: yAxisCurrentWidth < 30 ? -15 : (isSmallScreen? 0 : 10), // Корректировка левого отступа
              bottom: CHART_BOTTOM_PADDING 
            }}
            barCategoryGap={isSmallScreen ? "20%" : "25%"} 
          >
            <defs>
              <linearGradient id="barGradientRed" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#C62828" />
                <stop offset="100%" stopColor="#E53935" />
              </linearGradient>
              <linearGradient id="barGradientYellow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F57F17" />
                <stop offset="100%" stopColor="#FFB300" />
              </linearGradient>
              <linearGradient id="barGradientGreen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1B5E20" />
                <stop offset="100%" stopColor="#388E3C" />
              </linearGradient>
            </defs>
            {/* <CartesianGrid stroke={UI_COLORS.gridStroke} strokeDasharray="3 3" horizontal={true} vertical={false} /> */}
            {/* Убираем сетку полностью для чистоты, как на экспортном PNG */}
            <XAxis type="number" hide={true} /> 
            <YAxis 
              type="category"
              dataKey="name"
              width={yAxisCurrentWidth} 
              tickLine={false} 
              axisLine={false} 
              tick={<CustomYAxisTick 
                      isSmallScreen={isSmallScreen} 
                      verySmallScreen={verySmallScreen} 
                    />}
              interval={0} 
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(204,204,204,0.05)' }} />
            <Bar dataKey="percentage" barSize={BAR_HEIGHT} radius={[0, 3, 3, 0]}> 
              { !verySmallScreen && (
                <LabelList 
                  dataKey="displayPercentage" 
                  position="right" 
                  offset={8}
                  style={{ fill: UI_COLORS.textMuted, fontSize: isSmallScreen? 9 : 11 }} 
                  formatter={(value: string) => `${value}%`}
                />
              )}
              {items?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getGradientId(entry.category)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {(totalViolations > 0 || dateDisplayInfo) && (
        <div style={styles.infoBlocksContainer}>
          {totalViolations > 0 && (
            <div style={styles.totalBlock}>
              <TotalViolationsIcon />
              <span style={styles.totalText}>
                Всего не выполнили норму:&nbsp;<span style={styles.totalNumber}>{totalViolations} {pluralize(totalViolations, ['паблик', 'паблика', 'пабликов'])}</span>
              </span>
            </div>
          )}
          {dateDisplayInfo && <div style={styles.dateBlock}>{dateDisplayInfo}</div>}
        </div>
      )}

      {compliantFoundersList && compliantFoundersList.length > 0 && (
        <div style={styles.compliantBlock}>
          <div style={styles.compliantHeader}>
            <CompliantIcon />
            <span style={styles.compliantTitle}>Выполнили норму все паблики:</span>
          </div>
          <div style={styles.compliantList}>
            {compliantFoundersList.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsiveComplianceChart; 
